// src/features/activity/PreferencesTab.tsx
// Personal inbox preferences: grouping, default filter, per-scope mute rules.
// GetActivitySettings, UpdateActivitySettings.

import { useEffect, useState } from 'react';
import { observer } from '@react';
import { useI18n } from '@react/hooks';
import { activityActivitySettingStore } from '@core/stores';
import { Button, Input, ErrorAlert } from '@react/components';
import { Select } from '@/components/Select';
import { ToggleSwitch } from '@/components/ToggleSwitch';
import type { ActivitySettings, ActivitySubscription } from '@core/types/activity';
import { INBOX_FILTERS, errMessage } from './logic';

export const PreferencesTab = observer(() => {
  const { t } = useI18n();
  const [settings, setSettings] = useState<ActivitySettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    setLoading(true);
    activityActivitySettingStore.GetActivitySettings()
      .then((res) => setSettings(res.settings ?? { group_by_entity: true, default_filter: 'all', subscriptions: [], user_id: '' }))
      .catch((e: unknown) => setError(errMessage(e, t('activity.settings.preferences.loadError'))))
      .finally(() => setLoading(false));
  }, [t]);

  const patch = (partial: Partial<ActivitySettings>) => {
    setSettings((prev) => (prev ? { ...prev, ...partial } : prev));
    setSaved(false);
  };

  const addSubscription = () => {
    const next: ActivitySubscription = { scope: 'ACTIVITY_SCOPE_PROJECT', scope_id: '', muted: false, muted_verbs: [], muted_categories: [] };
    patch({ subscriptions: [...(settings?.subscriptions ?? []), next] });
  };

  const updateSubscription = (idx: number, partial: Partial<ActivitySubscription>) => {
    if (!settings) return;
    const next = settings.subscriptions.map((s, i) => (i === idx ? { ...s, ...partial } : s));
    patch({ subscriptions: next });
  };

  const removeSubscription = (idx: number) => {
    if (!settings) return;
    patch({ subscriptions: settings.subscriptions.filter((_, i) => i !== idx) });
  };

  const save = async () => {
    if (!settings) return;
    setSaving(true);
    setError(null);
    try {
      const res = await activityActivitySettingStore.UpdateActivitySettings({ settings });
      setSettings(res.settings ?? settings);
      setSaved(true);
    } catch (e) {
      setError(errMessage(e, t('activity.settings.preferences.saveError')));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="h-32 animate-pulse rounded-md bg-muted" aria-busy="true" />;
  if (!settings) return null;

  return (
    <div className="max-w-2xl space-y-5">
      {error && <ErrorAlert error={error} dismissible onDismiss={() => setError(null)} />}
      {saved && <p className="text-sm text-success">{t('activity.settings.preferences.saved')}</p>}

      <ToggleSwitch
        label={t('activity.settings.preferences.groupByEntity')}
        checked={settings.group_by_entity}
        onChange={(v) => patch({ group_by_entity: v })}
      />

      <Select
        label={t('activity.settings.preferences.defaultFilter')}
        value={settings.default_filter || 'all'}
        onChange={(e) => patch({ default_filter: e.target.value })}
        className="w-56"
        options={INBOX_FILTERS.map((f) => ({ value: f, label: t(`activity.inbox.filter.${f}`) }))}
      />

      <div>
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-semibold text-foreground">{t('activity.settings.preferences.subscriptions')}</h3>
            <p className="text-xs text-muted-foreground">{t('activity.settings.preferences.subscriptionsHelp')}</p>
          </div>
          <Button size="sm" variant="secondary" onClick={addSubscription}>{t('activity.settings.preferences.addSubscription')}</Button>
        </div>

        <div className="mt-2 space-y-2">
          {settings.subscriptions.map((sub, idx) => (
            <div key={idx} className="flex flex-wrap items-end gap-2 rounded-md border border-border p-2">
              <Select
                label={t('activity.feed.scope.label')}
                value={sub.scope ?? 'ACTIVITY_SCOPE_PROJECT'}
                onChange={(e) => updateSubscription(idx, { scope: e.target.value as ActivitySubscription['scope'] })}
                className="w-36"
                options={[
                  { value: 'ACTIVITY_SCOPE_PROJECT', label: t('activity.feed.scope.project') },
                  { value: 'ACTIVITY_SCOPE_TEAM', label: t('activity.feed.scope.team') },
                  { value: 'ACTIVITY_SCOPE_TENANT', label: t('activity.feed.scope.tenant') },
                ]}
              />
              <Input
                label={t('activity.feed.scopeId')}
                value={sub.scope_id}
                onChange={(e) => updateSubscription(idx, { scope_id: e.target.value })}
                className="w-44"
                fullWidth={false}
              />
              <ToggleSwitch
                label={t('activity.settings.preferences.muted')}
                checked={sub.muted}
                onChange={(v) => updateSubscription(idx, { muted: v })}
                className="w-40"
              />
              <Button size="sm" variant="ghost" onClick={() => removeSubscription(idx)}>{t('common.remove')}</Button>
            </div>
          ))}
        </div>
      </div>

      <Button onClick={save} isLoading={saving}>{t('activity.settings.preferences.save')}</Button>
    </div>
  );
});

export default PreferencesTab;
